import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box as MuiBox } from "@mui/material";
import { DataGridPro as MuiDataGrid } from "@mui/x-data-grid-pro";
import { useAlerts } from "common";
import { LoadingGridOverlay } from "common/components/DataGrid/LoadingGridOverlay";
import { NoRowsGridOverlay } from "common/components/DataGrid/NoRowsGridOverlay";
import { getProspectCohortHistory } from "app/services/prospectServices";
import { CohortHistoryDefaults, LOADER_TYPES } from "app/shared/constants";
import { defaultTableColumnHeaders } from "./ProspectDetailsCohortHistoryConfig";
import { tableStyles } from "app/shared/UI/sharedStyles";

const ProspectDetailsCohortHistory = ({ mrn }) => {
  const { t } = useTranslation();
  const { setAlert } = useAlerts();
  const [cohortHistory, setCohortHistory] = useState([]);
  const [cohortHistoryFilters, setCohortHistoryFilters] = useState({
    sort: `${CohortHistoryDefaults.COHORT_HISTORY_DEFAULT_ORDERBY},${CohortHistoryDefaults.COHORT_HISTORY_DEFAULT_ORDER}`,
    page: CohortHistoryDefaults.COHORT_HISTORY_DEFAULT_PAGE,
    size: CohortHistoryDefaults.COHORT_HISTORY_DEFAULT_PAGESIZE,
  });
  const [sort, setSort] = useState({
    order: CohortHistoryDefaults.COHORT_HISTORY_DEFAULT_ORDER,
    orderBy: CohortHistoryDefaults.COHORT_HISTORY_DEFAULT_ORDERBY,
  });
  const [page, setPage] = useState(
    cohortHistoryFilters.page ||
      CohortHistoryDefaults.COHORT_HISTORY_DEFAULT_PAGE
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    cohortHistoryFilters.size ||
      CohortHistoryDefaults.COHORT_HISTORY_DEFAULT_PAGESIZE
  );
  const [cohortHistoryTotalCount, setCohortHistoryTotalCount] = useState(0);

  const initialLoading = {
    prospectDetailsCohortHistory: true,
  };

  const [isLoading, setIsLoading] = useState(initialLoading);
  // Pseudo-reducer to keep loading states updated
  const setLoader = (type, isSet) => {
    setIsLoading((prev) => ({
      ...prev,
      [type]: isSet,
    }));
  };

  useEffect(() => {
    getProspectCohortHistory(
      mrn,
      setCohortHistory,
      cohortHistoryFilters,
      setAlert,
      setCohortHistoryTotalCount,
      setLoader
    );
  }, [mrn, setAlert, cohortHistoryFilters]);

  const NoStudyOverlay = () => (
    <NoRowsGridOverlay
      message={!cohortHistory.length && t("common.emptyTableRow")}
    />
  );

  const handleSortChange = (sortModel) => {
    setSort({
      orderBy: sortModel[0].field,
      order: sortModel[0].sort,
    });

    setCohortHistoryFilters({
      ...cohortHistoryFilters,
      sort: `${sortModel[0].field},${sortModel[0].sort}`,
    });
  };

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
    setCohortHistoryFilters({
      ...cohortHistoryFilters,
      page: model.page,
      size: model.pageSize,
    });
  };

  return (
    <MuiBox
      id="pep-my-studies-table-box"
      sx={{ width: "100%", overflow: "auto" }}
    >
      <MuiDataGrid
        slots={{
          loadingOverlay: LoadingGridOverlay,
          noRowsOverlay: NoStudyOverlay,
        }}
        rows={cohortHistory}
        getRowId={(row) => row.studyId}
        columns={defaultTableColumnHeaders(t)}
        autoHeight
        pagination
        disableColumnMenu
        disableColumnResize
        disableColumnReorder
        disableMultipleColumnsSorting
        disableRowSelectionOnClick
        loading={isLoading[LOADER_TYPES.PROSPECT_DETAILS_COHORT_HISTORY]}
        paginationMode="server"
        sortingMode="server"
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={{
          page: page,
          pageSize: rowsPerPage,
        }}
        rowCount={cohortHistoryTotalCount}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={[{ field: sort.orderBy, sort: sort.order }]}
        sortingOrder={["asc", "desc"]}
        onSortModelChange={handleSortChange}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd"
        }
        sx={tableStyles}
        columnHeaderHeight={60}
        getRowHeight={() => "auto"}
      />
    </MuiBox>
  );
};

export default ProspectDetailsCohortHistory;
